/**
 * Tracks in-flight PageList fetch requests so stale responses can be ignored.
 */
export function createFetchRequestTracker() {
  let latestRequestId = 0;

  return {
    begin() {
      latestRequestId += 1;
      return latestRequestId;
    },
    isCurrent(requestId) {
      return requestId === latestRequestId;
    },
  };
}

/**
 * Applies fetch results only when the response still matches the latest request.
 */
export function applyFetchResultIfCurrent({
  requestId,
  tracker,
  onApply,
}) {
  if (!tracker.isCurrent(requestId)) return false;
  onApply();
  return true;
}

/**
 * Runs a PageList fetch and applies success/error/empty only for the latest request.
 * This is the same control flow used by PageList.fetchData.
 */
export async function executeTrackedPageListFetch({
  tracker,
  fetchFn,
  onSuccess,
  onError,
  onEmpty,
}) {
  const requestId = tracker.begin();

  try {
    const data = await fetchFn();
    if (data === undefined) {
      return applyFetchResultIfCurrent({
        requestId,
        tracker,
        onApply: () => onEmpty?.(),
      });
    }

    return applyFetchResultIfCurrent({
      requestId,
      tracker,
      onApply: () => onSuccess?.(data),
    });
  } catch (error) {
    return applyFetchResultIfCurrent({
      requestId,
      tracker,
      onApply: () => onError?.(error),
    });
  }
}
